<template>
  <div>
    <PurchaseFlowTabs
      :tabs="personalInfoTabs"
      :active-index="currentSubstepIndex"
      @tab-click="handleTabClick"
      :is-review="is_review"
      :is-modal-edit="is_modal_edit"
    />
    <div
      v-if="personalInfoTabs[currentSubstepIndex].name === 'Mailing Address' && !is_review"
      @click="handleMailingSameAsResidentialChange"
      class="bg-container-bg py-5 flex items-center px-3 mb-[0.875rem] border-2 rounded-xl cursor-pointer mt-5 md:mt-6"
      :class="
        purchaseFlowStore.mailing_same_as_residential
          ? 'border-radio-checkbox-outline'
          : 'border-container-bg'
      "
    >
      <div class="mr-5">
        <Icon
          v-if="purchaseFlowStore.mailing_same_as_residential"
          name="CircleCheck"
          class="w-6 h-6 fill-radio-checkbox-checked dark:fill-white stroke-container-bg"
        />
        <Icon v-else name="Circle" class="w-6 h-6 stroke-radio-checkbox-outline" />
      </div>

      <div class="font-inter text-lg font-medium md:font-semibold text-strongest">
        Same as residential address
      </div>
    </div>
    <div
      v-if="personalInfoTabs[currentSubstepIndex].name === 'ID Information'"
      class="mt-5 md:mt-6 animate-slideup"
    >
      <AsteriskNotice variant="info" size="md" class="mb-1" text="Important Disclaimer" contained>
        <div class="font-inter text-strongest font-medium">
          Please provide the details from your government-issued ID, such as a passport or driver's
          license.
        </div>
      </AsteriskNotice>
    </div>
    <div
      v-if="
        personalInfoTabs[currentSubstepIndex].name === 'ID Information' && daysUntilIDExpires <= 30
      "
      class="mb-[1.625rem] mt-5 md:mt-6 animate-slideup"
    >
      <AsteriskNotice variant="error" size="md" class="mb-1" text="ID Info Expiring" contained>
        <div class="font-inter text-strongest font-medium" v-if="daysUntilIDExpires > 0">
          Your ID is expiring in {{ daysUntilIDExpires }} days. Please provide updated information.
        </div>
        <div class="font-inter text-strongest font-medium" v-else>
          Your ID has expired. Please provide updated information.
        </div>
      </AsteriskNotice>
    </div>
    <div
      :class="
        (personalInfoTabs[currentSubstepIndex].name === 'Mailing Address' && !is_review) ||
        (personalInfoTabs[currentSubstepIndex].name === 'ID Information' &&
          daysUntilIDExpires <= 30)
          ? 'h-0'
          : 'h-4'
      "
    ></div>
    <Form
      :schema_array="schemaToUse"
      :state_object="stateObject"
      :record_id="dataStore.contact.record_id"
      :is_disabled="
        is_review ||
        (personalInfoTabs[currentSubstepIndex].name === 'Mailing Address' &&
          purchaseFlowStore.mailing_same_as_residential)
      "
      :variant="is_review ? 'primary' : 'secondary'"
      :class="
        personalInfoTabs[currentSubstepIndex].name === 'Mailing Address' && !is_review
          ? ''
          : 'mt-5 md:mt-6'
      "
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useSchemaStore, useDataStore, usePurchaseFlowStore, useUIStore } from "@/stores";
import { Form, Icon, AsteriskNotice } from "@/components";
import PurchaseFlowTabs from "./PurchaseFlowUtilities/PurchaseFlowTabs.vue";

const schemaStore = useSchemaStore();
const dataStore = useDataStore();
const UIStore = useUIStore();
const purchaseFlowStore = usePurchaseFlowStore();

const personalInfoTabs = [
  { name: "Personal Information", schema_group: "Personal Information" },
  { name: "Residential Address", schema_group: "Residential Address" },
  { name: "Mailing Address", schema_group: "Mailing Address" },
  { name: "ID Information", schema_group: "ID Information" },
];

const emit = defineEmits(["substep-change"]);
const props = defineProps({
  is_review: { type: Boolean, default: false },
  is_modal_edit: { type: Boolean, default: false },
});

const localSubstep = ref(1);

const currentSubstepIndex = computed(() => {
  if (props.is_review || props.is_modal_edit) return localSubstep.value - 1;
  return purchaseFlowStore.current_substep - 1;
});
const schemaToUse = computed(() => getSchemaForSubstep(currentSubstepIndex.value));
const stateObject = computed(() => getStateObjectForSubstep(currentSubstepIndex.value));
const daysUntilIDExpires = computed(() => {
  const id_expiration_date = dataStore.contact?.id_info?.id_expiration_date;
  if (!id_expiration_date) return 1000;
  const today = new Date();
  const expirationDate = new Date(id_expiration_date);
  const timeDiff = expirationDate.getTime() - today.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
});

function getSchemaForSubstep(index) {
  const tab = personalInfoTabs[index];
  return purchaseFlowStore.accredited_owner_contact_personal_info_schema.find(
    (schema) => schema.group_name === tab?.schema_group
  )?.fields;
}

function getStateObjectForSubstep(index) {
  const tab = personalInfoTabs[index];
  return tab?.name === "ID Information" ? dataStore.contact.id_info : dataStore.contact;
}

function handleTabClick(index) {
  const targetSubstep = index + 1;
  const currentSubstep = localSubstep.value;

  if (props.is_review) {
    localSubstep.value = targetSubstep;
    return;
  }

  if (props.is_modal_edit) {
    if (targetSubstep <= currentSubstep) {
      localSubstep.value = targetSubstep;
      return;
    }
    const result = validateRange(currentSubstep - 1, index);
    if (!result.proceed) {
      localSubstep.value = result.failedAt + 1;
      return;
    }

    localSubstep.value = targetSubstep;
    return;
  }

  emit("substep-change", targetSubstep);
}

function validateSubstep(index) {
  const schema = getSchemaForSubstep(index);

  const result = schemaStore.validateFields(
    dataStore.contact,
    schema,
    dataStore.contact.record_id,
    true
  );

  return {
    proceed: result.proceed,
    invalid_fields: result.invalid_fields,
    tabName: personalInfoTabs[index]?.name,
    error_messages: result.error_messages,
    field_errors: result.field_errors,
  };
}

function validateRange(fromIndex, toIndex) {
  for (let i = fromIndex; i < toIndex; i++) {
    const result = validateSubstep(i);
    if (!result.proceed) {
      return {
        proceed: result.proceed,
        failedAt: i,
        tabName: result.tabName,
        invalid_fields: result.invalid_fields,
        error_messages: result.error_messages,
        field_errors: result.field_errors,
      };
    }
  }
  return { proceed: true };
}

defineExpose({
  validate() {
    const result = validateSubstep(currentSubstepIndex.value);
    if (result.field_errors?.ssn === "The SSN is already in use.") {
      UIStore.animateNotificationAlert({
        message: "The SSN is already in use. Reach out to support for assistance.",
        type: "error",
        does_persist: true,
      });
    }
    // if (!result.proceed) console.log("Validation failed:", result.invalid_fields);
    return result;
  },
  validateRange,
  afterContinue: async () => await purchaseFlowStore.saveOwnerProgress(),
  substepCount: personalInfoTabs.length,
});

function handleMailingSameAsResidentialChange() {
  purchaseFlowStore.mailing_same_as_residential = !purchaseFlowStore.mailing_same_as_residential;
  if (purchaseFlowStore.mailing_same_as_residential) {
    syncMailingToResidential();
  }
}

function syncMailingToResidential() {
  dataStore.contact.ma_street1 = dataStore.contact.street1;
  dataStore.contact.ma_street2 = dataStore.contact.street2;
  dataStore.contact.ma_city = dataStore.contact.city;
  dataStore.contact.ma_state = dataStore.contact.state;
  dataStore.contact.ma_zip_code = dataStore.contact.zip_code;
  dataStore.contact.ma_country = dataStore.contact.country;
}

watch(
  () => [
    dataStore.contact.street1,
    dataStore.contact.street2,
    dataStore.contact.city,
    dataStore.contact.state,
    dataStore.contact.zip_code,
    dataStore.contact.country,
  ],
  () => {
    if (purchaseFlowStore.mailing_same_as_residential) {
      syncMailingToResidential();
    }
  }
);
</script>
